/**
 * Reads identity hints from the host site (cookies, localStorage, window
 * globals) based on a tenant-supplied source map. Every read is wrapped in
 * try/catch so a missing cookie or undefined global path never breaks the
 * widget — this is always best-effort defense-in-depth, never required.
 */

export type SourceFrom = 'cookie' | 'localStorage' | 'window';

export interface DataSource {
  target: 'name' | 'email' | 'business' | 'external_id';
  from: SourceFrom;
  key: string;
}

const MAX_VALUE_LENGTH = 500;

/**
 * Reads each configured source and returns a flat object keyed by `target`.
 *
 * Silent fail per source (missing cookie / undefined window path). Values are
 * stringified and truncated to {@link MAX_VALUE_LENGTH} characters. If two
 * sources point at the same `target`, the last one wins.
 */
export function readSourced(
  sources: readonly DataSource[] | undefined,
): Record<string, string> {
  if (typeof window === 'undefined' || !sources?.length) return {};
  const out: Record<string, string> = {};
  for (const s of sources) {
    const raw = readOne(s);
    if (raw == null) continue;
    const value = String(raw).slice(0, MAX_VALUE_LENGTH);
    if (value.length === 0) continue;
    out[s.target] = value;
  }
  return out;
}

function readOne(s: DataSource): unknown {
  try {
    if (s.from === 'cookie') return readCookie(s.key);
    if (s.from === 'localStorage') return window.localStorage?.getItem(s.key);
    if (s.from === 'window') {
      return getByPath(window as unknown as Record<string, unknown>, s.key);
    }
    return null;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  const cookies = document.cookie?.split(';') ?? [];
  for (const c of cookies) {
    const i = c.indexOf('=');
    if (i < 0) continue;
    const k = c.slice(0, i).trim();
    if (k === name) return decodeURIComponent(c.slice(i + 1).trim());
  }
  return null;
}

/**
 * Dot-path getter for the window object.
 *
 * Example: to read `window.currentUser.email`, pass `'currentUser.email'`.
 */
function getByPath(root: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let node: unknown = root;
  for (const p of parts) {
    if (node == null || typeof node !== 'object') return null;
    node = (node as Record<string, unknown>)[p];
  }
  return node;
}
